import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Command } from 'cmdk';
import { Search } from 'lucide-react';
import { useCommandPalette } from '@/hooks/useCommandPalette';
import { useRoleFilter } from '@/hooks/useRoleFilter';
import { RoleFilter } from '@/components/RoleFilter';
import { cn } from '@/lib/utils';

type SearchDoc = {
  id: string;
  url: string;
  title: string;
  description: string;
  section: string;
  personas: string;
  kind: 'chapter' | 'overview' | 'standalone';
};

type LoadedIndex = {
  miniSearch: import('minisearch').default<SearchDoc>;
  docs: SearchDoc[];
};

let indexCache: Promise<LoadedIndex> | null = null;

async function loadIndex(): Promise<LoadedIndex> {
  if (indexCache) return indexCache;
  indexCache = (async () => {
    const [{ default: MiniSearch }, payload] = await Promise.all([
      import('minisearch'),
      import('@/content/search-index.json').then((m) => m.default as {
        docs: SearchDoc[];
        serialized: string;
      }),
    ]);
    const miniSearch = MiniSearch.loadJSON<SearchDoc>(payload.serialized, {
      idField: 'id',
      fields: ['title', 'description', 'body', 'section', 'personas', 'headings'],
      storeFields: ['url', 'title', 'description', 'section', 'personas', 'kind'],
    });
    return { miniSearch, docs: payload.docs };
  })();
  return indexCache;
}

export function CommandPalette() {
  const { open, setOpen } = useCommandPalette();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [loaded, setLoaded] = useState<LoadedIndex | null>(null);
  // Shared site-wide role filter. Empty means "All roles" (no filter).
  const [selected, setSelected] = useRoleFilter();
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen(!open);
      } else if (e.key === 'Escape' && open) {
        setOpen(false);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, setOpen]);

  useEffect(() => {
    if (!open || loaded) return;
    let cancelled = false;
    loadIndex().then((idx) => {
      if (!cancelled) setLoaded(idx);
    });
    return () => {
      cancelled = true;
    };
  }, [open, loaded]);

  useEffect(() => {
    if (open) requestAnimationFrame(() => inputRef.current?.focus());
  }, [open]);

  const activeSet = useMemo(() => new Set<string>(selected), [selected]);

  const results = useMemo(() => {
    if (!loaded) return [] as SearchDoc[];
    const all = query.trim()
      ? (loaded.miniSearch.search(query, {
          prefix: true,
          fuzzy: 0.2,
          boost: { title: 3, headings: 2, description: 1.5 },
        }) as unknown as (SearchDoc & { score: number })[])
      : loaded.docs.map((d) => ({ ...d, score: 0 }));
    const filtered =
      activeSet.size === 0
        ? all
        : all.filter((d) => {
            if (!d.personas) return false;
            return d.personas.split(/\s+/).some((p) => activeSet.has(p));
          });
    return filtered.slice(0, 50);
  }, [loaded, query, activeSet]);

  const grouped = useMemo(() => {
    const map = new Map<string, SearchDoc[]>();
    for (const r of results) {
      const section = r.section || 'Other';
      if (!map.has(section)) map.set(section, []);
      map.get(section)!.push(r);
    }
    return Array.from(map.entries());
  }, [results]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Search"
      className="fixed inset-0 z-50 flex items-start justify-center bg-ink/40 backdrop-blur-sm p-4 pt-[10vh]"
      onClick={() => setOpen(false)}
    >
      <div
        className={cn(
          'w-full max-w-2xl rounded-lg border border-rule bg-bg-elev shadow-lg overflow-hidden',
          'animate-fade-in',
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <Command label="Site search" className="flex flex-col" shouldFilter={false}>
          <div className="flex items-center gap-3 border-b border-rule px-4 py-3">
            <Search className="h-4 w-4 text-ink-mute" strokeWidth={1.75} aria-hidden="true" />
            <Command.Input
              ref={inputRef}
              value={query}
              onValueChange={setQuery}
              placeholder="Search the field guide..."
              className="flex-1 bg-transparent text-md text-ink placeholder:text-ink-mute outline-none"
            />
            <kbd className="hidden sm:inline rounded border border-rule px-1.5 py-0.5 font-mono text-[10px] text-ink-mute">
              Esc
            </kbd>
          </div>

          <RoleFilter
            selected={selected}
            onChange={setSelected}
            className="border-b border-rule px-4 py-3"
          />

          <Command.List
            className="max-h-[60vh] overflow-y-auto px-2 py-3"
            aria-live="polite"
          >
            {!loaded ? (
              <div className="px-2 py-6 text-sm text-ink-mute">Loading index...</div>
            ) : grouped.length === 0 ? (
              <Command.Empty className="px-2 py-6 text-sm text-ink-mute">
                No results.
              </Command.Empty>
            ) : (
              grouped.map(([section, items]) => (
                <Command.Group
                  key={section}
                  heading={section}
                  className="mb-2 [&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:py-2 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider [&_[cmdk-group-heading]]:text-ink-mute"
                >
                  {items.map((doc) => (
                    <Command.Item
                      key={doc.id}
                      value={`${doc.title} ${doc.description} ${doc.url}`}
                      onSelect={() => {
                        setOpen(false);
                        navigate(doc.url);
                      }}
                      className="flex flex-col gap-0.5 rounded-md px-3 py-2 text-sm cursor-pointer data-[selected=true]:bg-accent-soft data-[selected=true]:text-ink"
                    >
                      <span className="font-medium text-ink">{doc.title}</span>
                      {doc.description ? (
                        <span className="text-xs text-ink-mute line-clamp-1">
                          {doc.description}
                        </span>
                      ) : null}
                    </Command.Item>
                  ))}
                </Command.Group>
              ))
            )}
          </Command.List>
        </Command>
      </div>
    </div>
  );
}
